<script lang="ts">
  import { scheduleUndo } from '$lib/state/actionDialog.svelte';
  // A small, reusable comment thread — list + post + delete-own — shared by
  // LeadDetail and CompanyDetail (see GET/POST/DELETE /api/comments). Modeled
  // on IssueDetailPanel's comment feed, but standalone (no attachments/status
  // history, since those are issue-specific).
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Send, Trash2 } from 'lucide-svelte';
  import { commentsApi } from '$lib/api/crm';
  import { session } from '$lib/state/session.svelte';
  import { getErrorMessage } from '$lib/api/client';

  const { entityType, entityId }: { entityType: 'lead' | 'company'; entityId: number } = $props();

  const queryClient = useQueryClient();

  const commentsQuery = createQuery(() => ({
    queryKey: ['comments', entityType, entityId],
    queryFn: () => commentsApi.list(entityType, entityId)
  }));

  let text = $state('');
  const postMutation = createMutation(() => ({
    mutationFn: (body: string) => commentsApi.create(entityType, entityId, body),
    onSuccess: () => {
      text = '';
      queryClient.invalidateQueries({ queryKey: ['comments', entityType, entityId] });
    }
  }));
  const deleteMutation = createMutation(() => ({
    mutationFn: (id: number) => commentsApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['comments', entityType, entityId] })
  }));

  function submit(e: SubmitEvent) {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    postMutation.mutate(body);
  }

  function remove(id: number) {
    scheduleUndo(`Delete comment #${id}`, () => deleteMutation.mutate(id));
  }

  function timeAgo(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  }
</script>

<div class="tym-card px-[18px] py-4">
  <div class="mb-2.5 text-[11px] font-semibold uppercase tracking-[.08em] text-toyama-teal">Comments</div>

  <form class="mb-3.5 flex items-end gap-2.5" onsubmit={submit}>
    <textarea
      bind:value={text}
      placeholder="Write a comment…"
      rows="1"
      class="input min-h-[42px] max-h-[120px] flex-1 resize-none"
    ></textarea>
    <button
      type="submit"
      class="flex h-[42px] shrink-0 items-center gap-1.5 rounded-[11px] bg-toyama-lime px-4 font-heading text-[13px] font-bold text-navy hover:bg-[var(--tym-lime-600)] disabled:opacity-50"
      disabled={postMutation.isPending || !text.trim()}
      aria-label="Post comment"
    >
      <Send class="h-4 w-4" />
    </button>
  </form>

  {#if commentsQuery.isLoading}
    <p class="tym-loading-state">Loading comments&hellip;</p>
  {:else if commentsQuery.isError}
    <p class="tym-error-state">{getErrorMessage(commentsQuery.error)}</p>
  {:else}
    <div class="flex flex-col gap-2.5">
      {#each commentsQuery.data?.results ?? [] as cm (cm.id)}
        <div class="flex gap-2.5">
          <span
            class="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full font-heading text-[10.5px] font-bold"
            style="background:#E3F0F1; color:#226A73"
          >
            {(cm.author_name || '?').slice(0, 2).toUpperCase()}
          </span>
          <div class="flex-1 rounded-tl-[4px] rounded-tr-xl rounded-br-xl rounded-bl-xl border border-[#EFEEED] bg-toyama-light-carbon px-3.5 py-2.5">
            <div class="flex items-baseline gap-2">
              <span class="text-[12.5px] font-bold text-navy">{cm.author_name || 'Unknown'}</span>
              <span class="text-[11px] text-dark-gray">{timeAgo(cm.created_at)}</span>
              <span class="flex-1"></span>
              {#if cm.user_id !== null && cm.user_id === session.me?.user.id}
                <button
                  type="button"
                  class="text-dark-gray hover:text-red-600"
                  onclick={() => remove(cm.id)}
                  aria-label="Delete comment"
                >
                  <Trash2 class="h-3.5 w-3.5" />
                </button>
              {/if}
            </div>
            <div class="mt-0.5 whitespace-pre-wrap text-[13px] leading-[1.55] text-toyama-heavy-carbon">{cm.body}</div>
          </div>
        </div>
      {:else}
        <p class="py-1 text-[12.5px] text-dark-gray">No comments yet.</p>
      {/each}
    </div>
  {/if}
</div>
